import { BarChart3Icon } from 'lucide-react';
import { StatTile, TrendChart } from '../charts';
import { DataTable, EmptyState, Panel, SectionHeader, type DataTableHeader, type DataTableSort } from '../primitives';
import { SegmentedControl } from '../navigation';
import { useClientTable } from '../../hooks/useClientTable';

/** The ranges a partner can look back over, in days. */
export const SHOWCASE_RANGES = [7, 30, 90] as const;
export type ShowcaseRange = (typeof SHOWCASE_RANGES)[number];

/** GET /partner/stats (and the staff twin): a showcase over a range. */
export interface ShowcaseStatsData {
  readonly range: { readonly from: string; readonly to: string; readonly days: number };
  readonly totals: { readonly views: number; readonly visitors: number; readonly leads: number; readonly leadRate: number | null };
  readonly daily: readonly { readonly day: string; readonly views: number; readonly visitors: number; readonly leads: number }[];
  readonly activities: readonly { readonly websiteId: string; readonly name: string; readonly views: number; readonly visitors: number; readonly leads: number }[];
  readonly sources: readonly { readonly source: string; readonly views: number }[];
}

export interface ShowcasePerformanceProps {
  readonly stats: ShowcaseStatsData;
  readonly range: ShowcaseRange;
  readonly onRangeChange: (range: ShowcaseRange) => void;
  /** Who is reading: the partner ("your") or staff (the partner's name). */
  readonly subject?: string;
}

const RANGE_OPTIONS = SHOWCASE_RANGES.map((days) => ({ key: String(days) as `${ShowcaseRange}`, label: `${days} days` }));

const SORT_FIELDS = { name: 'name', views: 'views', visitors: 'visitors', leads: 'leads' } as const;
const SORT_KEYS = Object.values(SORT_FIELDS);
const DEFAULT_SORT: DataTableSort = { key: SORT_FIELDS.views, direction: 'desc' };

const count = (n: number): string => n.toLocaleString('en-US');
const percent = (rate: number | null): string => (rate === null ? 'None yet' : `${(rate * 100).toFixed(rate < 0.1 ? 1 : 0)}%`);
const shortDay = (day: string): string =>
  new Date(`${day}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

/**
 * How a partner's showcase is doing: views, unique visitors, leads and lead
 * rate over a range, the daily trend, which activities guests look at, and
 * the sites it was seen on. Counted on our server as pages are served, with
 * no cookies; visitors are unique per day.
 */
export function ShowcasePerformance({ stats, range, onRangeChange, subject = 'your' }: ShowcasePerformanceProps): React.ReactElement {
  const table = useClientTable({
    rows: stats.activities.map((a) => ({ ...a, id: a.websiteId })),
    searchText: (a) => a.name,
    sortKeys: SORT_KEYS,
    defaultSort: DEFAULT_SORT,
  });
  const headers: readonly DataTableHeader[] = [
    { label: 'Activity', sortKey: SORT_FIELDS.name },
    { label: 'Views', sortKey: SORT_FIELDS.views },
    { label: 'Visitors', sortKey: SORT_FIELDS.visitors },
    { label: 'Leads', sortKey: SORT_FIELDS.leads },
  ];
  const empty = stats.totals.views === 0 && stats.totals.leads === 0;
  const maxSource = Math.max(1, ...stats.sources.map((s) => s.views));

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-portal-small text-portal-muted">
          {shortDay(stats.range.from)} to {shortDay(stats.range.to)}. Visitors are counted once a day; no cookies are used.
        </p>
        <SegmentedControl
          label="Time range"
          options={RANGE_OPTIONS}
          value={String(range) as `${ShowcaseRange}`}
          onChange={(key) => onRangeChange(Number(key) as ShowcaseRange)}
        />
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile label="Views" value={count(stats.totals.views)} hint="Pages seen on the showcase" />
        <StatTile label="Visitors" value={count(stats.totals.visitors)} hint="Unique people, per day" />
        <StatTile label="Leads" value={count(stats.totals.leads)} hint="Quote requests sent" />
        <StatTile label="Lead rate" value={percent(stats.totals.leadRate)} hint="Leads per visitor" />
      </div>

      {empty ? (
        <Panel>
          <EmptyState
            icon={<BarChart3Icon aria-hidden className="size-6" />}
            title="No visits yet"
            message={`Once ${subject === 'your' ? 'your' : `${subject}'s`} showcase is on a website and guests open it, views, visitors and leads appear here.`}
          />
        </Panel>
      ) : (
        <>
          <div>
            <SectionHeader title="Visitors by day" description="Unique visitors each day, with leads in the tooltip." />
            <Panel className="p-4">
              <TrendChart
                title="Showcase visitors"
                unit="visitors"
                variant="line"
                height={220}
                points={stats.daily.map((d) => ({
                  key: d.day,
                  label: shortDay(d.day),
                  value: d.visitors,
                  details: [`${count(d.views)} views`, `${count(d.leads)} ${d.leads === 1 ? 'lead' : 'leads'}`],
                }))}
              />
            </Panel>
          </div>

          <div className="grid gap-8 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <div>
              <SectionHeader title="Activities" description="What guests opened, and the leads each brought." />
              {stats.activities.length === 0 ? (
                <Panel>
                  <EmptyState icon={<BarChart3Icon aria-hidden className="size-6" />} title="No activity pages opened yet" message="When a guest opens an activity, it shows here." />
                </Panel>
              ) : (
                <DataTable headers={headers} rightAlignedHeaders={[1, 2, 3]} {...(table.sort ? { sort: table.sort } : {})} onSort={table.onSort}>
                  {table.pageRows.map((a) => (
                    <tr key={a.websiteId} className="border-t border-portal-line align-middle">
                      <td className="p-3 text-portal-small font-bold">{a.name}</td>
                      <td className="p-3 text-right text-portal-small tabular-nums">{count(a.views)}</td>
                      <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">{count(a.visitors)}</td>
                      <td className="p-3 text-right text-portal-small tabular-nums">{count(a.leads)}</td>
                    </tr>
                  ))}
                </DataTable>
              )}
            </div>
            <div>
              <SectionHeader title="Where it was seen" description="The websites the showcase was opened on." />
              <Panel className="p-4">
                {stats.sources.length === 0 ? (
                  <p className="text-portal-small text-portal-muted">Only direct visits so far. Sites that embed the showcase appear here.</p>
                ) : (
                  <ul className="space-y-3">
                    {stats.sources.map((s) => (
                      <li key={s.source}>
                        <div className="flex items-baseline justify-between gap-3 text-portal-small">
                          <span className="truncate font-bold">{s.source}</span>
                          <span className="tabular-nums text-portal-muted">{count(s.views)}</span>
                        </div>
                        <div className="mt-1 h-1.5 rounded-full bg-portal-line">
                          <div className="h-1.5 rounded-full bg-[var(--portal-brand-primary,#1851d1)]" style={{ width: `${(s.views / maxSource) * 100}%` }} />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
